import { isNumberByRegex } from '@/shared/lib';
import _ from 'lodash';

const MAX_STUDY_ID = 2147483647; // 32-bit interger

/**
 * Keeps the study id field of a support request numeric while the user types. Accepts the new text only when it is empty or matches the numeric regex, otherwise keeps the previous value.
 *
 * @param currentValue - Value currently shown in the field.
 * @param newValue - Value the user just typed.
 * @returns The value the field should display.
 */
const filterStudyIdInput = (currentValue: any, newValue: any) => {
  if (newValue === '' || isNumberByRegex(newValue)) {
    return newValue;
  }
  return currentValue;
};

/**
 * Turns the study lookup result of a change-study-information request into a study and error pair. Reports INVALID when the first record carries no id, OPEN_REQUEST when that study already has a clinicTicket, and otherwise returns the study with a null error.
 *
 * @param result - Studies returned by the support study lookup.
 * @returns Object holding the accepted study or the rejection reason.
 */
const mapValidationResult = (result: any): any => {
  const study = result[0];
  if (!study?.id) {
    return { study: null, error: 'INVALID' };
  }
  if (!_.isEmpty(study.clinicTicket)) {
    return { study: null, error: 'OPEN_REQUEST' };
  }
  return { study, error: null };
};

/**
 * Decides whether the change-study-information support request may be submitted. Blocks submission while saving, when no study has been validated, when the message is blank, or when no reply address exists in either the committed email list or the pending input.
 *
 * @param validatedStudy - Study accepted by the id validation, if any.
 * @param message - Message body typed for the request.
 * @param emailList - Reply email addresses already committed.
 * @param emailInputValue - Reply email text not yet committed.
 * @param isSaving - Whether a submission is already running.
 * @returns True when the submit button must stay disabled.
 */
const isSubmitDisabled = ({
  validatedStudy,
  message,
  emailList,
  emailInputValue,
  isSaving,
}: any) => {
  if (isSaving) return true;
  if (!validatedStudy) return true;
  if (!message.trim()) return true;
  if (_.isEmpty(emailList) && !emailInputValue) return true;
  return false;
};

/**
 * Builds the payload passed to `handleSubmitSupportRequest`.
 *
 * @param category - Support request category.
 * @param selectedFacilityValue - Id of the facility the request is filed for.
 * @param validatedStudy - Study accepted by the id validation, whose id is sent.
 * @param message - Message body typed for the request.
 * @param emailList - Reply email addresses, joined with commas.
 * @returns The support request payload for the Bioflux app.
 */
const buildSubmitPayload = ({
  category,
  selectedFacilityValue,
  validatedStudy,
  message,
  emailList,
}: any) => ({
  category,
  replyEmail: _.join(emailList, ','),
  contents: [{ study: validatedStudy.id }],
  message: message.trim(),
  appName: 'Bioflux',
  facilityId: selectedFacilityValue,
});

export {
  MAX_STUDY_ID,
  filterStudyIdInput,
  mapValidationResult,
  isSubmitDisabled,
  buildSubmitPayload,
};
